// DesignStudioViewNew.tsx - Fixed version with all working handlers
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CommandCenterLayout from '../command-center/CommandCenterLayout';
import DesignStudioSkin from './DesignStudioSkin';
import AgentDashboardPanel from '../widgets/AgentDashboardPanel';
import SystemStatusWidget from '../widgets/SystemStatusWidget';
import GeneratorModal from '../modals/GeneratorModal';
import TestReportModal from '../modals/TestReportModal';
import DefectsModal from '../modals/DefectsModal';
import AIConfigWidget from '../widgets/AIConfigWidget';
import AdminConsole from '../auth/AdminConsole';
import MockDesignModal from '../modals/MockDesignModal';
import APIEndpointModal from '../modals/APIEndpointModal';
import { MockDesign, APIEndpoint } from '../../types';
import { AgentProfile, TestResult, AIConfig, ServiceStatus, AIModel, TestCaseDetails } from '../../types';
import '../../styles/command-center.css';

import APIManagementWidget from '../widgets/APIManagementWidget';
import ContainerManagementView from './ContainerManagementView';


// Get API base URL
const getApiBaseUrl = () => {
  // First check if explicitly set (for Docker and GCP)
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // Development fallback
  if (process.env.NODE_ENV === "development") {
    return "http://localhost:8000";
  }

  // Production: use hostname replacement only as last resort
  const hostname = window.location.hostname;

  // Check if we're on localhost (production build running locally)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return "http://localhost:8000";
  }

  // GCP production - replace frontend-alpha with orchestrator
  const apiHost = hostname.replace('frontend-alpha', 'orchestrator');
  return `https://${apiHost}`;
};

const API_BASE_URL = getApiBaseUrl();

interface DesignStudioViewNewProps {
  agents: AgentProfile[];
  selectedAgent: string;
  setSelectedAgent: (name: string) => void;
  testCases: string[];
  selectedTest: string;
  setSelectedTest: (test: string) => void;
  isGeneratingTests: boolean;
  setIsGeneratingTests: (generating: boolean) => void;
  fetchTestCases: () => void;
  aiConfig: AIConfig | null;
  addAgent: (agentData: Omit<AgentProfile, 'id' | 'testCaseCount'>) => void;
  updateAgent: (agentId: string, updates: Partial<AgentProfile>) => void;
  deleteAgent: (agentId: string) => void;
  logs: string[];
  setLogs: (logs: string[] | ((prev: string[]) => string[])) => void;
  setResults: (value: React.SetStateAction<Map<string, TestResult>>) => void;
  setIsDetailsModalOpen: (isOpen: boolean) => void;
  setCurrentTestDetails: (details: TestCaseDetails | null) => void;
  setIsDataModalOpen: (isOpen: boolean) => void;
  setEditableData: (data: TestCaseDetails | null) => void;
  setIsDbViewerModalOpen: (isOpen: boolean) => void;
  setDbTableData: (data: any[]) => void;
  setDbTableHeaders: (headers: string[]) => void;
  showUploadModal: boolean;
  setShowUploadModal: (show: boolean) => void;
  showTemplateLibrary: boolean;
  setShowTemplateLibrary: (show: boolean) => void;
  isUploading: boolean;
  setIsUploading: (uploading: boolean) => void;
  fetchIntelligentAgents: () => void;
  apis: any[];
  applications: any[];
  fetchEntities: () => Promise<any>;
  results: Map<string, TestResult>;
  setIsExecuteModalOpen: (isOpen: boolean) => void;
  isTestRunning: boolean;
  status: ServiceStatus | null;
  logFilters: { [key: string]: boolean };
  setLogFilters: (filters: { [key: string]: boolean }) => void;
  testwerkApiKey: string;
  setTestwerkApiKey: (key: string) => void;
  agentApiKey: string;
  setAgentApiKey: (key: string) => void;
  selectedModel: AIModel;
  setSelectedModel: (model: AIModel) => void;
  onUpdateAIConfig: () => void;
  onOpenDashboard: () => void;
  onOpenEnvironment: () => void;
  loadAgentTestResults: (agentName: string) => void;
  defects: any[];
  fetchDefects: (agentName: string) => void;
  lastDataRefresh: number;
  setLastDataRefresh: (value: number) => void;
  onStatusScopeChange?: (scope: 'agent' | 'api' | 'application') => void;
  onStatusAPISelect?: (api: string) => void;
  onStatusApplicationSelect?: (app: string) => void;
}

interface TestExecution {
  test_name: string;
  status: "PASSED" | "FAILED" | "ERROR" | "RUNNING";
  ai_confidence: number;
  completed_at: string;
  started_at: string;
  failure_reason?: string;
  agent_name: string;
}

const DesignStudioViewNew: React.FC<DesignStudioViewNewProps> = (props) => {
  const [useNewUI, setUseNewUI] = useState(true);
  const [defectsForAgent, setDefectsForAgent] = useState<any[]>([]);
  const [isTestCasesModalOpen, setIsTestCasesModalOpen] = useState(false);
  const [showGeneratorModal, setShowGeneratorModal] = useState(false);
  const [generatorModalMode, setGeneratorModalMode] = useState<'scenarios' | 'documents'>('scenarios');
  const [isTestDataModalOpen, setIsTestDataModalOpen] = useState(false);
  const [isTestReportModalOpen, setIsTestReportModalOpen] = useState(false);
  const [isDefectsModalOpen, setIsDefectsModalOpen] = useState(false);
  const [isSystemStatusPopupOpen, setIsSystemStatusPopupOpen] = useState(false);
  const [systemStatusMode, setSystemStatusMode] = useState<"environment" | "logs" | "results">("logs");
  const [isAPIModalOpen, setIsAPIModalOpen] = useState(false);
  const [isMockModalOpen, setIsMockModalOpen] = useState(false);
  const [isAIConfigOpen, setIsAIConfigOpen] = useState(false);
  const [isAgentInfoOpen, setIsAgentInfoOpen] = useState(false);
  const [isAdminPopupOpen, setIsAdminPopupOpen] = useState(false);
  const [agentTestCases, setAgentTestCases] = useState<any[]>([]);
  const [lastRunStatuses, setLastRunStatuses] = useState<Map<string, TestExecution>>(new Map());
  const [dbFilter, setDbFilter] = useState("all");

  // State for API and Mock functionality
  const [mockDesigns, setMockDesigns] = useState<MockDesign[]>([]);
  const [apiEndpoints, setApiEndpoints] = useState<APIEndpoint[]>([]);
  const [editingMock, setEditingMock] = useState<MockDesign | undefined>(undefined);
  const [editingEndpoint, setEditingEndpoint] = useState<APIEndpoint | undefined>(undefined);

  const [showAPIManagementModal, setShowAPIManagementModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<string>('all');
  const [entityType, setEntityType] = useState<'all' | 'agents' | 'apis'>('all');
  const [selectedEntity, setSelectedEntity] = useState<string>('all');
  //const [applications, setApplications] = useState<any[]>([]);
  const [availableAgents, setAvailableAgents] = useState<any[]>([]);
  const [availableAPIs, setAvailableAPIs] = useState<any[]>([]);

  // Adding scope management states RAMANA 11.09.2025
  const [currentScope, setCurrentScope] = useState<'agent' | 'api' | 'application'>('agent');
  const [selectedAPI, setSelectedAPI] = useState<string>('');

  const [isContainerManagementOpen, setIsContainerManagementOpen] = useState(false);




  const {
    agents,
    selectedAgent,
    setSelectedAgent,
    testCases,
    selectedTest,
    setSelectedTest,
    isGeneratingTests,
    setIsGeneratingTests,
    fetchTestCases,
    aiConfig,
    addAgent,
    updateAgent,
    deleteAgent,
    logs,
    setLogs,
    setResults,
    setIsDetailsModalOpen,
    setCurrentTestDetails,
    setIsDataModalOpen,
    setEditableData,
    setIsDbViewerModalOpen,
    setDbTableData,
    setDbTableHeaders,
    showUploadModal,
    setShowUploadModal,
    showTemplateLibrary,
    setShowTemplateLibrary,
    isUploading,
    setIsUploading,
    fetchIntelligentAgents,
    apis,
    applications,
    fetchEntities,
    results,
    setIsExecuteModalOpen,
    isTestRunning,
    status,
    logFilters,
    setLogFilters,
    testwerkApiKey,
    setTestwerkApiKey,
    agentApiKey,
    setAgentApiKey,
    selectedModel,
    setSelectedModel,
    onUpdateAIConfig,
    onOpenDashboard,
    onOpenEnvironment,
    loadAgentTestResults,
    defects,
    fetchDefects,
    lastDataRefresh,
    setLastDataRefresh,
    onStatusScopeChange,
    onStatusAPISelect,
    onStatusApplicationSelect
  } = props;

  const currentAgent = agents.find((a) => a.name === selectedAgent);
  //console.log('[Debug] Current agent in DesignStudioView:', currentAgent);
  //console.log('[Debug] AI Config in DesignStudioView:', aiConfig);
  const isTasksReady = !!aiConfig?.testwerk_api_key_configured && !!selectedAgent && !isGeneratingTests && (currentAgent?.testCaseCount ?? 0) > 0;

  // Load agent test cases
  const loadAgentTestCases = async (agentName: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/db/test-cases`);
      const agentTests = response.data.filter((tc: any) => tc.agent_name === agentName);
      setAgentTestCases(agentTests);
    } catch (error) {
      console.error("Error loading agent test cases:", error);
      setAgentTestCases([]);
    }
  };

  // Load last run statuses
  const loadLastRunStatuses = async (agentName: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/db/test-executions`);
      const allExecutions = response.data.filter((exec: TestExecution) => exec.agent_name === agentName);
      const latestExecutions = new Map<string, TestExecution>();
      allExecutions.forEach((exec: TestExecution) => {
        const testName = exec.test_name;
        const existing = latestExecutions.get(testName);
        const execTime = new Date(exec.completed_at || exec.started_at).getTime();
        const prevTime = existing ? new Date(existing.completed_at || existing.started_at).getTime() : -1;
        if (execTime > prevTime) latestExecutions.set(testName, exec);
      });
      setLastRunStatuses(latestExecutions);
    } catch (error) {
      console.error("Error loading last run statuses:", error);
      setLastRunStatuses(new Map());
    }
  };


  // Add function to load applications and entities
  const loadApplicationsAndEntities = async () => {
    try {
      // REMOVE THIS LINE - applications comes from props
      // const appsResponse = await axios.get(`${API_BASE_URL}/db/applications`);
      // setApplications(appsResponse.data || []);

      // Load agents
      const agentsResponse = await axios.get(`${API_BASE_URL}/agent-manager/agents/intelligent`);
      setAvailableAgents(agentsResponse.data.agents || []);

      // Load APIs
      const apisResponse = await axios.get(`${API_BASE_URL}/api/endpoints`);
      if (apisResponse.data.success) {
        setAvailableAPIs(apisResponse.data.endpoints || []);
      }

      // Applications are already available from props, no need to fetch again
      //console.log('Using applications from props:', applications);

    } catch (error) {
      console.error('Error loading entities:', error);
    }
  };

  // Handler functions for Mock functionality
  const handleSaveMock = (mock: MockDesign) => {
    if (editingMock) {
      setMockDesigns(mockDesigns.map(m => m.id === mock.id ? mock : m));
    } else {
      setMockDesigns([...mockDesigns, { ...mock, id: Date.now().toString() }]);
    }
    setIsMockModalOpen(false);
    setEditingMock(undefined);
  };

  const handleEditMock = (mock: MockDesign) => {
    setEditingMock(mock);
    setIsMockModalOpen(true);
  };

  const handleDeleteMock = (id: string) => {
    setMockDesigns(mockDesigns.filter(m => m.id !== id));
  };

  // Handler functions for API functionality
  const handleSaveEndpoint = async (endpoint: any) => {
    try {
      // Check if this is a platform integration (JIRA, Confluence, Database, etc.)
      const isPlatformIntegration = ['Database', 'Jira', 'Confluence', 'Databricks', 'Bitbucket'].includes(endpoint.type);

      if (isPlatformIntegration) {
        // Platform integrations go to a different endpoint
        const integrationConfig = {
          name: endpoint.name,
          type: endpoint.type,
          url: endpoint.url,
          authentication: endpoint.authentication,
          configuration: endpoint.configuration,
          is_active: endpoint.isActive
        };

        // TODO: Call platform integration endpoint (to be created)
        const response = await axios.post(`${API_BASE_URL}/api/platform-integrations`, integrationConfig);

        if (response.data.success) {
          alert(`Integration "${endpoint.name}" saved successfully!`);
          setIsAPIModalOpen(false);
          await fetchEntities();  // Refresh all entities including APIs
          // Optionally refresh integrations list
        }
      } else {
        // Test target APIs go through the existing flow
        /*
        const apiConfig = {
          id: endpoint.id,
          api_name: endpoint.name,
          base_url: endpoint.url,
          type: endpoint.type,  // ADD THIS LINE
          selected_domain: endpoint.configuration?.domain || 'general',
          auth_type: endpoint.authentication?.type || 'none',
          auth_credentials: endpoint.authentication?.credentials || {},
          headers: endpoint.configuration?.headers || {},
          endpoints: endpoint.parsed_endpoints || [],
          mock_fallback: false,
          selected_templates: [],
          custom_documents: endpoint.custom_documents || [],
          parsed_endpoints: endpoint.parsed_endpoints || [],
          parsed_schemas: endpoint.parsed_schemas || {},
          application_name: endpoint.application_name
        };
        */

        const apiConfig = {
          id: endpoint.id,
          api_name: endpoint.name,
          base_url: endpoint.url,
          type: endpoint.type,
          selected_domain: endpoint.selected_domain || endpoint.configuration?.domain || 'general',
          auth_type: endpoint.authentication?.type || 'none',
          auth_credentials: endpoint.authentication?.credentials || {},
          headers: endpoint.configuration?.headers || {},
          endpoints: endpoint.parsed_endpoints || [],
          mock_fallback: false,
          selected_templates: endpoint.selected_templates || [],  // ← USE ACTUAL DATA
          use_template: endpoint.use_template || false,           // ← ADD THIS TOO
          custom_documents: endpoint.custom_documents || [],
          parsed_endpoints: endpoint.parsed_endpoints || [],
          parsed_schemas: endpoint.parsed_schemas || {},
          application_name: endpoint.application_name
        };


        console.log('🔍 DesignStudioViewNew - Sending to backend:');
        console.log('endpoint.selected_templates:', endpoint.selected_templates);
        console.log('endpoint.use_template:', endpoint.use_template);
        console.log('final apiConfig.selected_templates:', apiConfig.selected_templates);
        //console.log('Sending to backend:', apiConfig);
        //console.log('Endpoint type:', endpoint.type);
        // Call the existing backend to register the API endpoint
        const response = await axios.post(`${API_BASE_URL}/configure-api-testing`, apiConfig);

        if (response.data.success) {
          // Store locally for UI display
          if (editingEndpoint) {
            setApiEndpoints(apiEndpoints.map(e => e.id === endpoint.id ? endpoint : e));
          } else {
            const newEndpoint = {
              ...endpoint,
              id: response.data.endpoint_id || Date.now().toString(),
              created_at: new Date().toISOString()
            };
            setApiEndpoints([...apiEndpoints, newEndpoint]);
          }

          alert(`API endpoint "${endpoint.name}" configured successfully!`);
          setIsAPIModalOpen(false);

          await fetchEntities();  // Refresh all entities including APIs

          // TODO: Open Generator Modal for test generation
          // setSelectedAgent(endpoint.name);
          // setIsGeneratorModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Failed to save endpoint:', error);
      alert(`Failed to save: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleEditEndpoint = (endpoint: APIEndpoint) => {
    console.log('DesignStudioViewNew - editing endpoint:', endpoint);
    setEditingEndpoint(endpoint);
    setIsAPIModalOpen(true);
  };

  const handleDeleteEndpoint = (id: string) => {
    setApiEndpoints(apiEndpoints.filter(e => e.id !== id));
  };

  // Helper functions for enhanced agents
  const getDomainIcon = (domain?: string) => {
    switch (domain) {
      case 'telecommunications': return '📡';
      case 'ecommerce': return '🛒';
      case 'finance': return '💰';
      case 'healthcare': return '🏥';
      default: return '🤖';
    }
  };

  const getDomainColor = (domain?: string) => {
    switch (domain) {
      case 'telecommunications': return '#2196F3';
      case 'ecommerce': return '#FF9800';
      case 'finance': return '#4CAF50';
      case 'healthcare': return '#E91E63';
      default: return '#757575';
    }
  };

  // Get status badge function
  const getStatusBadge = (testName: string) => {
    const lastRun = lastRunStatuses.get(testName);
    if (!lastRun) {
      return (
        <span style={{
          padding: '0.25rem 0.5rem',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          backgroundColor: '#f8f9fa',
          color: '#6c757d',
          border: '1px solid #dee2e6'
        }}>
          Never Run
        </span>
      );
    }

    const statusColors = {
      PASSED: { bg: '#d4edda', color: '#155724', border: '#c3e6cb' },
      FAILED: { bg: '#f8d7da', color: '#721c24', border: '#f5c6cb' },
      ERROR: { bg: '#f8d7da', color: '#721c24', border: '#f5c6cb' },
      RUNNING: { bg: '#fff3cd', color: '#856404', border: '#ffeaa7' }
    };

    const colors = statusColors[lastRun.status] || statusColors.ERROR;

    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        backgroundColor: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem'
      }}>
        {lastRun.status}
        {lastRun.ai_confidence && (
          <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>
            ({lastRun.ai_confidence}%)
          </span>
        )}
      </span>
    );
  };

  // Run single test function
  const runSingleTest = async (testName: string) => {
    //console.log(`Running test: ${testName}`);
    alert(`Single test execution for "${testName}" - Coming Soon!`);
  };

  // Export test cases function
  const handleExportTestCases = async (agentName: string) => {
    try {
      // Collect all test cases for the agent
      const testCasesResponse = await axios.get(`${API_BASE_URL}/db/test-cases`);
      const agentTests = testCasesResponse.data.filter((tc: any) => tc.agent_name === agentName);

      // Collect test execution results
      const executionsResponse = await axios.get(`${API_BASE_URL}/db/test-executions`);
      const agentExecutions = executionsResponse.data.filter((exec: any) => exec.agent_name === agentName);

      // Collect defects
      const defectsResponse = await axios.get(`${API_BASE_URL}/db/defects`, {
        params: { agent_name: agentName }
      });
      const agentDefects = defectsResponse.data;

      // Get agent metadata
      const agent = agents.find(a => a.name === agentName);

      // Create export object
      const exportData = {
        export_info: {
          agent_name: agentName,
          export_timestamp: new Date().toISOString(),
          export_version: "1.0"
        },
        agent_metadata: agent || {},
        test_cases: agentTests,
        test_results: agentExecutions,
        defects: agentDefects,
        summary: {
          total_test_cases: agentTests.length,
          total_executions: agentExecutions.length,
          total_defects: agentDefects.length,
          test_types: [...new Set(agentTests.map((tc: any) => tc.type))],
          last_export: new Date().toLocaleString()
        }
      };

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${agentName}_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      //console.log('Export completed successfully');
    } catch (error) {
      //console.error('Error exporting test cases:', error);
      alert('Error exporting test data. Please check the console for details.');
    }
  };
  const getFilteredTestCases = () => {
    let filtered = [...agentTestCases];

    // Filter by entity type
    if (entityType === 'agents') {
      // Show only test cases from agents
      filtered = filtered.filter(tc => {
        // Check if this test belongs to an agent (not an API)
        const isFromAgent = availableAgents.some(agent => agent.name === tc.agent_name);
        return isFromAgent;
      });

      // Further filter by selected agent if not "all"
      if (selectedEntity !== 'all') {
        filtered = filtered.filter(tc => tc.agent_name === selectedEntity);
      }
    } else if (entityType === 'apis') {
      // Show only test cases from APIs
      filtered = filtered.filter(tc => {
        // Check if this test belongs to an API (not an agent)
        const isFromAPI = availableAPIs.some(api => api.name === tc.agent_name);
        return isFromAPI;
      });

      // Further filter by selected API if not "all"
      if (selectedEntity !== 'all') {
        filtered = filtered.filter(tc => tc.agent_name === selectedEntity);
      }
    }
    // If entityType === 'all', show everything

    // Filter by test type
    if (dbFilter !== 'all') {
      filtered = filtered.filter(tc => tc.type === dbFilter);
    }

    return filtered;
  };



  const handleOpenTestCasesModal = async (entityName?: string, entityType: 'agent' | 'api' | 'application' = 'agent') => {
    await loadApplicationsAndEntities();

    // Load ALL test cases, not just for one agent
    try {
      const response = await axios.get(`${API_BASE_URL}/db/test-cases`);
      setAgentTestCases(response.data);

      // Load defects data
      const defectsResponse = await axios.get(`${API_BASE_URL}/db/defects`);
      setDefectsForAgent(defectsResponse.data);

      // Load last run statuses for all tests
      //await loadLastRunStatuses('');


      // Load ALL test execution statuses for all tests
      try {
        const execResponse = await axios.get(`${API_BASE_URL}/db/test-executions`);
        const latestStatuses = new Map<string, TestExecution>();
        execResponse.data.forEach((exec: TestExecution) => {
          const testName = exec.test_name;
          const existing = latestStatuses.get(testName);
          const execTime = new Date(exec.completed_at || exec.started_at).getTime();
          const prevTime = existing ? new Date(existing.completed_at || existing.started_at).getTime() : -1;
          if (execTime > prevTime) {
            latestStatuses.set(testName, exec);
          }
        });
        setLastRunStatuses(latestStatuses);
      } catch (error) {
        console.error("Error loading test execution statuses:", error);
        setLastRunStatuses(new Map());
      }


      // Set the entity based on the type passed
      if (entityName) {
        setSelectedEntity(entityName);
        // Map the entity type to the format expected by the modal
        if (entityType === 'agent') {
          setEntityType('agents');
        } else if (entityType === 'api') {
          setEntityType('apis');
        } else if (entityType === 'application') {
          setEntityType('applications');
        }
      }
    } catch (error) {
      console.error("Error loading test cases:", error);
      setAgentTestCases([]);
    }

    setIsTestCasesModalOpen(true);
  };


  // PROPER HANDLERS
  /*
  const handleOpenTestCasesModal = async (agentName?: string) => {
    await loadApplicationsAndEntities();

    // Load ALL test cases, not just for one agent
    try {
      const response = await axios.get(`${API_BASE_URL}/db/test-cases`);
      setAgentTestCases(response.data);

      // If specific agent passed, set it as selected
      if (agentName) {
        setSelectedEntity(agentName);
        setEntityType('agents');
      }
    } catch (error) {
      console.error("Error loading test cases:", error);
      setAgentTestCases([]);
    }

    setIsTestCasesModalOpen(true);
  };
  */

  const handleOpenTestDataModal = async (entityName: string, entityType: 'agent' | 'api' | 'application' = 'agent') => {
    // For now, only load data for agents
    // APIs and Applications will need different data loading logic
    if (entityType === 'agent') {
      await loadAgentTestCases(entityName);
      await loadLastRunStatuses(entityName);
    } else if (entityType === 'api') {
      // TODO: Load API test data
      await loadAgentTestCases(entityName); // Temporary - use same logic
      // In future: await loadAPITestCases(entityName);
    } else if (entityType === 'application') {
      // TODO: Load Application test data
      await loadAgentTestCases(entityName); // Temporary - use same logic
      // In future: await loadApplicationTestCases(entityName);
    }

    setIsTestDataModalOpen(true);
  };


  /*
    const handleOpenTestDataModal = async (agentName: string) => {
      await loadAgentTestCases(agentName);
      await loadLastRunStatuses(agentName);
      setIsTestDataModalOpen(true);
    };
  */
  const handleOpenDbViewer = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/db/test-cases`);
      const data = response.data;
      if (data && data.length > 0) {
        setDbTableHeaders(Object.keys(data[0]));
        setDbTableData(data);
      } else {
        setDbTableHeaders(["No Data"]);
        setDbTableData([{ "No Data": "No test cases found in database" }]);
      }
      setIsDbViewerModalOpen(true);
    } catch (error: any) {
      alert(`Could not load DB table data: ${error.message}`);
    }
  };

  const handleViewDetails = (testCase: TestCaseDetails) => {
    setCurrentTestDetails(testCase);
    setIsDetailsModalOpen(true);
  };

  const handleEditTestData = (testCase: TestCaseDetails) => {
    setEditableData(testCase);
    setIsDataModalOpen(true);
  };

  // Delete agent handler  
  const handleDeleteAgent = async (agentName: string) => {
    try {
      // Check if the agent has test cases
      const response = await axios.get(`${API_BASE_URL}/db/test-cases`);
      const agentTests = response.data.filter((tc: any) => tc.agent_name === agentName);

      let confirmMessage = `Are you sure you want to delete "${agentName}"?`;

      if (agentTests.length > 0) {
        confirmMessage = `Agent "${agentName}" has ${agentTests.length} test cases. Do you want to export your data first?\n\nClick OK to export data, or Cancel to proceed without export.`;

        // Offer to export first
        if (window.confirm(confirmMessage)) {
          await handleExportTestCases(agentName);
          // After export, ask for final confirmation
          if (!window.confirm(`Data exported. Now delete agent "${agentName}" and all its ${agentTests.length} test cases?`)) {
            return;
          }
        } else {
          // Ask for final confirmation without export
          confirmMessage = `This will permanently delete agent "${agentName}" and all ${agentTests.length} test cases, execution results, and defects.\n\nThis action cannot be undone. Continue?`;
          if (!window.confirm(confirmMessage)) {
            return;
          }
        }
      } else {
        if (!window.confirm(confirmMessage)) {
          return;
        }
      }

      // Find the agent ID from the agents list
      const agent = agents.find(a => a.name === agentName);
      if (!agent) {
        alert('Agent not found');
        return;
      }

      // Call delete API endpoint
      //const deleteResponse = await axios.delete(`${API_BASE_URL}/agents/${agent.id}`);
      // CORRECT - Goes through orchestrator proxy
      const deleteResponse = await axios.delete(`${API_BASE_URL}/agent-manager/agents/${agent.id}`);

      if (deleteResponse.data.success) {
        alert(`Agent "${agentName}" deleted successfully`);
        // Refresh agents list
        fetchIntelligentAgents();
        // Clear selection if this agent was selected
        if (selectedAgent === agentName) {
          setSelectedAgent('');
        }
      } else {
        alert('Failed to delete agent: ' + deleteResponse.data.message);
      }
    } catch (error: any) {
      console.error('Error deleting agent:', error);
      alert('Error deleting agent: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Handler for CommandHub actions
  const handleActionClick = (action: string) => {
    switch (action) {
      // System Controls
      case 'ai-config':
        setIsAIConfigOpen(true);
        break;
      case 'environment':
        onOpenEnvironment();
        break;
      case 'logs':
        setSystemStatusMode('logs');
        setIsSystemStatusPopupOpen(true);
        break;
      case 'database':
        handleOpenDbViewer();
        break;

      // Agent Management
      case 'upload-agent':
        setShowUploadModal(true);
        break;
      case 'delete-agent':
        if (selectedAgent) handleDeleteAgent(selectedAgent);
        break;

      // Primary Actions
      case 'scenarios':
        if (currentScope === 'agent' && selectedAgent) {
          handleOpenTestCasesModal(selectedAgent);
        } else if (currentScope === 'api' && selectedAPI) {
          handleOpenTestCasesModal(selectedAPI, 'api');
        } else if (currentScope === 'application' && selectedApplication) {
          handleOpenTestCasesModal(selectedApplication, 'application');
        }
        break;
      case 'execute':
        setIsExecuteModalOpen(true);
        break;

      // Tools
      case 'data':
        if (currentScope === 'agent' && selectedAgent) {
          handleOpenTestDataModal(selectedAgent);
        } else if (currentScope === 'api' && selectedAPI) {
          handleOpenTestDataModal(selectedAPI, 'api');
        } else if (currentScope === 'application' && selectedApplication) {
          handleOpenTestDataModal(selectedApplication, 'application');
        }
        break;
      case 'report':
        setIsTestReportModalOpen(true);
        break;
      case 'defects':
        setIsDefectsModalOpen(true);
        break;

      // Quick Access
      case 'dashboard':
        onOpenDashboard();
        break;
      case 'results':
        setSystemStatusMode('results');
        setIsSystemStatusPopupOpen(true);
        break;
      case 'generator':
        setGeneratorModalMode('scenarios');
        setShowGeneratorModal(true);
        break;
      case 'document-processor':
        setGeneratorModalMode('documents');
        setShowGeneratorModal(true);
        break;
      case 'admin':
        setIsAdminPopupOpen(true);
        break;

      case 'create-api':
        setIsAPIModalOpen(true);
        break;
      case 'create-mock':
        setIsMockModalOpen(true);
        break;

      case 'view-apis':
        setShowAPIManagementModal(true);
        break;

      case 'manage-containers':
        setIsContainerManagementOpen(true);
        break;

      case 'view-app':
        // For now, just log it -  can add proper modal later
        console.log('View application details for:', selectedApplication);
        alert(`Application: ${selectedApplication}\nThis feature is coming soon!`);
        break;

      case 'manage-app':
        // For now, just log it -  can add proper modal later  
        console.log('Manage applications');
        alert('Application management feature coming soon!');
        break;

      default:
        console.log('Unknown action:', action);
    }
  };

  // Filtered test cases
  const filteredTestCases = dbFilter === 'all' ? agentTestCases : agentTestCases.filter(tc => tc.type === dbFilter);

  // Old ribbon props
  const skinProps = {
    agents,
    selectedAgent,
    currentAgent,
    aiConfig,
    isGeneratingTests,
    isTestRunning,
    isTasksReady,
    logsCount: logs.length,
    resultsCount: results.size,
    onSelectAgent: setSelectedAgent,
    onUploadAgent: () => setShowUploadModal(true),
    onOpenTemplates: () => setShowTemplateLibrary(true),
    onOpenTestCases: () => handleOpenTestCasesModal(selectedAgent),
    onGenerateTests: () => console.log('Generate'),
    onOpenTestData: () => handleOpenTestDataModal(selectedAgent),
    onOpenDb: handleOpenDbViewer,
    onExecute: () => setIsExecuteModalOpen(true),
    onOpenReport: () => setIsTestReportModalOpen(true),
    onOpenDefects: () => setIsDefectsModalOpen(true),
    onOpenResults: () => {
      setSystemStatusMode("results");
      setIsSystemStatusPopupOpen(true);
    },
    onOpenLogs: () => {
      setSystemStatusMode('logs');
      setIsSystemStatusPopupOpen(true);
    },
    onOpenApiModal: () => setIsAPIModalOpen(true),
    onOpenMockModal: () => setIsMockModalOpen(true),
    onOpenAIConfig: () => setIsAIConfigOpen(true),
    onOpenAgentInfo: () => setIsAgentInfoOpen(true),
    onOpenGenerator: () => setShowGeneratorModal(true),
    onOpenDashboard,
    onOpenEnvironment,
  };

  return (
    <>
      {/* UI Toggle Button */}
      {/* <div style={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        zIndex: 1000
      }}>
        
        <button
          onClick={() => setUseNewUI(!useNewUI)}
          style={{
            padding: '3px 3px',
            borderRadius: '8px',
            background: useNewUI ? '#06b6d4' : '#dc2626',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '300',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        >
          {useNewUI ? '🆕 NEW UI' : '📦 OLD UI'}
        </button>
        
      </div>
      */}
      {/* Conditional Rendering */}
      {useNewUI ? (
        <CommandCenterLayout
          agents={agents}
          selectedAgent={selectedAgent}
          onSelectAgent={setSelectedAgent}
          apis={apis}
          applications={applications}
          onActionClick={handleActionClick}
          isGeneratingTests={isGeneratingTests}
          isTestRunning={isTestRunning}
          currentAgent={currentAgent}
          results={results}
          defects={defects}
          aiConfig={aiConfig}
          lastDataRefresh={lastDataRefresh}
          currentScope={currentScope}
          //onScopeChange={setCurrentScope}
          selectedAPI={selectedAPI}
          onScopeChange={(scope) => {
            setCurrentScope(scope);  // Update local state
            onStatusScopeChange?.(scope);  // Also update parent
          }}
          onSelectAPI={(api) => {
            setSelectedAPI(api);  // Update local state
            onStatusAPISelect?.(api);  // Also update parent
          }}
          selectedApplication={selectedApplication}
          onSelectApplication={(app) => {
            setSelectedApplication(app);  // Update local state
            onStatusApplicationSelect?.(app);  // Also update parent
          }}
        />
      ) : (
        <div className="ds-layout dashboard-layout">
          <DesignStudioSkin {...skinProps} />
          <AgentDashboardPanel
            selectedAgent={selectedAgent}
            currentAgent={currentAgent}
            results={results}
            defects={defects}
            isCollapsed={false}
            onToggleCollapse={() => { }}
          />
        </div>
      )}

      {/* Enhanced Test Cases Modal with Domain Context */}
      {isTestCasesModalOpen && (
        <div className="modal-overlay" onClick={() => setIsTestCasesModalOpen(false)}>
          <div className="modal-content fullscreen-modal" onClick={(e) => e.stopPropagation()}>

            {/* Enhanced Modal Header */}
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Test Scenarios Management</h2>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <button className="pushable push-primary btn-sm" onClick={() => handleOpenTestCasesModal()} disabled={isGeneratingTests}>
                  <span className="shadow"></span>
                  <span className="edge"></span>
                  <span className="front">Refresh All</span>
                </button>
                <button onClick={() => setIsTestCasesModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
              </div>
            </div>

            {/* New Filter Section */}
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 1fr', gap: '1rem', alignItems: 'center' }}>

                {/* Application Selector */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 'bold' }}>
                    Application:
                  </label>
                  <select
                    value={selectedApplication}
                    onChange={(e) => setSelectedApplication(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                  >
                    <option value="all">All Applications</option>
                    {applications.map((app: any) => (
                      <option key={app.id} value={app.id}>{app.name}</option>
                    ))}
                  </select>
                </div>

                {/* Entity Type Selector */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 'bold' }}>
                    Type:
                  </label>
                  <select
                    value={entityType}
                    onChange={(e) => {
                      setEntityType(e.target.value as any);
                      setSelectedEntity('all');
                    }}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                  >
                    <option value="all">All Types</option>
                    <option value="agents">Agents Only</option>
                    <option value="apis">APIs Only</option>
                  </select>
                </div>

                {/* Entity Selector */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 'bold' }}>
                    {entityType === 'agents' ? 'Agent:' : entityType === 'apis' ? 'API:' : 'Entity:'}
                  </label>
                  <select
                    value={selectedEntity}
                    onChange={(e) => setSelectedEntity(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                  >
                    <option value="all">All {entityType === 'agents' ? 'Agents' : entityType === 'apis' ? 'APIs' : 'Entities'}</option>
                    {entityType === 'agents' || entityType === 'all' ?
                      availableAgents.map((agent: any) => (
                        <option key={agent.id || agent.name} value={agent.name}>
                          {agent.name} {agent.enhanced_mode ? '⭐' : ''}
                        </option>
                      )) : null
                    }
                    {entityType === 'apis' || entityType === 'all' ?
                      availableAPIs.map((api: any) => (
                        <option key={api.id} value={api.name}>
                          {api.name} ({api.domain})
                        </option>
                      )) : null
                    }
                  </select>
                </div>

                {/* Test Type Filter */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 'bold' }}>
                    Test Type:
                  </label>
                  <select
                    value={dbFilter}
                    onChange={(e) => setDbFilter(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                  >
                    <option value="all">All Test Types</option>
                    <option value="Functional">Functional</option>
                    <option value="Integration">Integration</option>
                    <option value="Reliability">Reliability</option>
                    <option value="Edge Cases">Edge Cases</option>
                    <option value="Guardrail">Guardrail</option>
                    <option value="Compliance">Compliance</option>
                  </select>
                </div>
              </div>

              {/* Summary Stats */}
              <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#6c757d' }}>
                Showing {getFilteredTestCases().length} of {agentTestCases.length} total test cases
                {selectedEntity !== 'all' && ` for ${selectedEntity}`}
              </div>
            </div>

            {/* Test Cases Table */}
            <div style={{ height: '60vh', overflow: 'auto' }}>
              <table className="results-table">
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd' }}>Entity</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd' }}>Test Name</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd' }}>Type</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd' }}>Last Status</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd' }}>Defects</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd' }}>Task Prompt</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd' }}>Expected Outcome</th>
                    <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredTestCases().map((tc, index) => {
                    const isAPI = availableAPIs.some(api => api.name === tc.agent_name);
                    return (
                      <tr key={tc.id || index} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              backgroundColor: isAPI ? '#e3f2fd' : '#f3e5f5',
                              color: isAPI ? '#1565c0' : '#6a1b9a'
                            }}>
                              {isAPI ? 'API' : 'Agent'}
                            </span>
                            <span style={{ fontSize: '0.85rem' }}>{tc.agent_name}</span>
                          </div>
                        </td>
                        <td style={{ padding: '0.5rem', fontSize: '0.85rem' }}>{tc.name}</td>
                        <td style={{ padding: '0.5rem' }}>
                          <span className={`test-type-badge ${tc.type?.toLowerCase().replace(/\s+/g, '-')}`}>
                            {tc.type}
                          </span>
                        </td>
                        <td style={{ padding: '0.5rem' }}>{getStatusBadge(tc.name)}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          {(() => {
                            const associatedDefect = defectsForAgent.find(d =>
                              d.test_name === tc.name && d.agent_name === tc.agent_name
                            );

                            if (associatedDefect) {
                              return (
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsTestCasesModalOpen(false);
                                    setTimeout(() => setIsDefectsModalOpen(true), 100);
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    color: '#dc3545',
                                    fontWeight: 'bold',
                                    textDecoration: 'underline'
                                  }}
                                  title={`View Defect: ${associatedDefect.defect_id_str}`}
                                >
                                  {associatedDefect.defect_id_str}
                                </span>
                              );
                            }
                            return <span style={{ color: '#6c757d' }}>-</span>;
                          })()}
                        </td>
                        <td style={{ padding: '0.5rem', fontSize: '0.8rem', maxWidth: '300px' }}>
                          {tc.task_prompt?.substring(0, 100)}...
                        </td>
                        <td style={{ padding: '0.5rem', fontSize: '0.8rem', maxWidth: '300px' }}>
                          {tc.expected_outcome?.substring(0, 100)}...
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button className="pushable push-secondary btn-sm btn-xs" onClick={() => handleViewDetails(tc)}>
                              <span className="shadow"></span>
                              <span className="edge"></span>
                              <span className="front">View</span>
                            </button>
                            <button className="pushable push-success btn-sm btn-xs" onClick={() => runSingleTest(tc.name)}>
                              <span className="shadow"></span>
                              <span className="edge"></span>
                              <span className="front">Run</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {getFilteredTestCases().length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        No test cases found for the selected filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="modal-actions">
              <button onClick={() => setIsTestCasesModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Test Data Modal */}
      {isTestDataModalOpen && (
        <div className="modal-overlay" onClick={() => setIsTestDataModalOpen(false)}>
          <div className="modal-content fullscreen-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Test Data - {selectedAgent}</h2>
            <div style={{ height: '70vh', overflow: 'auto' }}>
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Mock Data</th>
                    <th>Expected Outcome</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agentTestCases.map((tc, index) => (
                    <tr key={index}>
                      <td>{tc.name}</td>
                      <td>{tc.type}</td>
                      <td><pre>{JSON.stringify(tc.mock_data, null, 2).substring(0, 100)}...</pre></td>
                      <td>{tc.expected_outcome?.substring(0, 100)}...</td>
                      <td>
                        <button onClick={() => handleEditTestData(tc)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-actions">
              <button onClick={() => setIsTestDataModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* AI Config Modal */}
      {isAIConfigOpen && (
        <div className="modal-overlay" onClick={() => setIsAIConfigOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>AI Configuration</h2>
            <AIConfigWidget
              aiConfig={aiConfig}
              testwerkApiKey={testwerkApiKey}
              setTestwerkApiKey={setTestwerkApiKey}
              agentApiKey={agentApiKey}
              setAgentApiKey={setAgentApiKey}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              onUpdateAIConfig={onUpdateAIConfig}
            />
          </div>
        </div>
      )}

      {/* System Status Widget */}
      {isSystemStatusPopupOpen && (
        <div className="modal-overlay" onClick={() => setIsSystemStatusPopupOpen(false)}>
          <div className="modal-content fullscreen-modal" onClick={(e) => e.stopPropagation()}>
            <SystemStatusWidget
              mode={systemStatusMode}
              logs={logs}
              results={results}
              onClose={() => setIsSystemStatusPopupOpen(false)}
              logFilters={logFilters}
              onLogFilterToggle={(filterName) => {
                setLogFilters(prev => ({
                  ...prev,
                  [filterName]: !prev[filterName]
                }));
              }}
              selectedAgent={selectedAgent}
            />
          </div>
        </div>
      )}

      {/* Generator Modal */}
      {showGeneratorModal && (
        <GeneratorModal
          isOpen={showGeneratorModal}
          onClose={() => setShowGeneratorModal(false)}
          selectedAgent={selectedAgent}
          fetchTestCases={fetchTestCases}
          isGenerating={isGeneratingTests}
          setIsGenerating={setIsGeneratingTests}
          mode={generatorModalMode}
          onDataRefresh={() => {
            setLastDataRefresh(Date.now());
            fetchEntities();  // ADDED THIS LINE to refresh entity counts
          }}
        />
      )}

      {/* Test Report Modal */}
      {isTestReportModalOpen && (
        <TestReportModal
          isOpen={isTestReportModalOpen}
          onClose={() => setIsTestReportModalOpen(false)}
          agentName={selectedAgent}
        />
      )}

      {/* Defects Modal */}
      {isDefectsModalOpen && (
        <DefectsModal
          isOpen={isDefectsModalOpen}
          onClose={() => setIsDefectsModalOpen(false)}
          selectedAgent={selectedAgent}
          onDefectUpdate={() => fetchDefects(selectedAgent)}
        />
      )}
      {/* Mock Design Modal */}
      {isMockModalOpen && (
        <MockDesignModal
          isOpen={isMockModalOpen}
          onClose={() => {
            setIsMockModalOpen(false);
            setEditingMock(undefined);
          }}
          onSave={handleSaveMock}
          editingMock={editingMock}
        />
      )}

      {/* API Endpoint Modal editingEndpoint={editingEndpoint} */}
      {isAPIModalOpen && (
        <APIEndpointModal
          isOpen={isAPIModalOpen}
          onClose={() => {
            setIsAPIModalOpen(false);
            setEditingEndpoint(undefined);
          }}
          onSave={handleSaveEndpoint}
          endpoint={editingEndpoint}
        />
      )}

      {/* API Management Modal - Add this right before the closing </> */}
      {showAPIManagementModal && (
        <div className="modal-overlay" onClick={() => setShowAPIManagementModal(false)}>
          <div className="modal-content fullscreen-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>API Endpoint Management</h2>
              <button onClick={() => setShowAPIManagementModal(false)}>✕</button>
            </div>
            <APIManagementWidget
              onCreateNew={() => {
                setShowAPIManagementModal(false);
                setIsAPIModalOpen(true);
              }}
              onEdit={(endpoint) => {
                setEditingEndpoint(endpoint);
                setShowAPIManagementModal(false);
                setIsAPIModalOpen(true);
              }}
              onRefresh={() => {
                fetchEntities();
              }}
            />
          </div>
        </div>
      )}

      {/* CONTAINER MANAGEMENT MODAL */}
      {isContainerManagementOpen && (
        <div className="modal-overlay" onClick={() => setIsContainerManagementOpen(false)}>
          <div className="modal-content fullscreen-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Container Management</h2>
              <button
                onClick={() => setIsContainerManagementOpen(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}
              >
                ✕
              </button>
            </div>
            <ContainerManagementView />
          </div>
        </div>
      )}

      {/* Admin Console */}
      {isAdminPopupOpen && (
        <AdminConsole onClose={() => setIsAdminPopupOpen(false)} />
      )}
    </>
  );
};

export default DesignStudioViewNew;
